import { Box, Checkbox, List, ListItem, Typography } from '@mui/joy';
import { FunctionComponent, ReactNode, useId } from 'react';

type CheckboxGroupProps<T extends string> = {
  label?: string;
  options: T[];
  values: Record<T, boolean>;
  onChange: (option: T) => void;
  orientation?: 'horizontal' | 'vertical';
  children?: ReactNode;
} & (
  | {
      i18n: Record<T, string>;
      optionLabel?: never;
    }
  | { i18n?: never; optionLabel: FunctionComponent<{ option: T }> }
);

export function CheckboxGroup<T extends string>({
  label,
  options,
  values,
  i18n,
  optionLabel,
  onChange,
  orientation,
  children,
}: CheckboxGroupProps<T>) {
  const id = useId();

  return (
    <Box>
      {label && (
        <Typography id={id} mb={1} fontWeight="bold">
          {label}
        </Typography>
      )}
      <Box role="group" aria-labelledby={id}>
        <List size="sm" orientation={orientation} wrap>
          {options.map((option) => (
            <ListItem key={option}>
              <Checkbox
                sx={{ alignItems: 'center' }}
                label={(i18n && i18n[option]) || (optionLabel && optionLabel({ option }))}
                checked={values[option]}
                onChange={() => onChange(option)}
              />
            </ListItem>
          ))}
        </List>
      </Box>
      {children}
    </Box>
  );
}
